import { ChevronDownIcon } from '@heroicons/react/16/solid';
import {
  ArrowLeftStartOnRectangleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/20/solid';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { motion } from 'framer-motion';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import useDebounce from 'src/shared/hooks/useDebounce';
import useImpersonationUsers from 'src/shared/hooks/useImpersonationUsers';

import {
  Autocomplete,
  AutocompleteItem,
  HeroButton as Button,
  cn,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure
} from 'src/shared/components/ui';

import { Modal, Typography } from '../../ui';
import UserAvatar from '../../UserAvatar';

interface UserMenuProps {
  isOpen: boolean;
  isMobile?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ isOpen = true, isMobile }) => {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const { isOpen: impersonateOpen, onOpen, onOpenChange } = useDisclosure();
  const impersonation = useAuthStore((state) => state.impersonation);
  const [search, setSearch] = useState('');
  const [selectedUsername, setSelectedUsername] = useState(impersonation ?? '');
  const debouncedSearch = useDebounce(search, 300);
  const { data } = useImpersonationUsers(debouncedSearch);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const setImpersonation = useAuthStore((state) => state.setImpersonation);
  const setSelectedOrganization = useAuthStore(
    (state) => state.setSelectedOrganization
  );
  const [loading, setLoading] = useState(false);

  const cleanedData = useMemo(() => {
    if (data) {
      return data.map((item: any) => ({
        label: item.text,
        value: item.id,
        archived: item.text.endsWith(' (Archived)')
      }));
    }

    return [];
  }, [data]);

  useEffect(() => {
    if (!shouldRefetch) return;

    setTimeout(() => {
      window.location.href = '/products';
    }, 300);
  }, [shouldRefetch]);

  const onLogout = async () => {
    logout();
    navigate('/login');
  };

  const handleSelectionChange = (key: any) => {
    const user = cleanedData.find((val) => key === val.value?.toString());

    if (user) {
      // Extract just the username (before the organization part if present)
      const username = user.label.includes('(')
        ? user.label.split(' (')[0]
        : user.label;

      setSelectedUsername(username);
      setSearch('');
    }
  };

  const onImpersonate = () => {
    setLoading(true);
    setImpersonation(selectedUsername);
    setSelectedOrganization(null);
    setShouldRefetch(true);
  };

  const username = useMemo(() => {
    if (user?.username) {
      const nameLength = user?.username?.length > 17;
      const truncateTitle = nameLength
        ? user?.username.slice(0, 17) + '...'
        : user?.username;

      return truncateTitle;
    }

    return null;
  }, [user?.username]);

  return (
    <>
      <Dropdown>
        <DropdownTrigger>
          <div
            className={cn(
              'p-4 flex gap-2 items-center justify-between hover:opacity-70 transition-opacity cursor-pointer',
              isMobile && 'p-0 justify-end'
            )}
          >
            <div className="flex gap-2 items-center">
              <UserAvatar
                size="lg"
                name={(user?.username || user?.firstName) as string}
                image={user?.avatar}
              />
              {!isMobile && (
                <motion.div
                  initial={!isOpen && { maxWidth: 0, opacity: 0 }}
                  animate={{
                    maxWidth: isOpen ? '100%' : 0,
                    opacity: isOpen ? 1 : 0
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <Typography variant="body-1" weight="medium">
                    {username}
                  </Typography>
                </motion.div>
              )}
            </div>
            {!isMobile && (
              <ChevronDownIcon className="size-4 text-secondary-text" />
            )}
          </div>
        </DropdownTrigger>
        <DropdownMenu
          itemClasses={{ base: 'data-[hover]:bg-gray-4' }}
          aria-label="User Actions"
        >
          {user?.isStaff || user?.impersonating ? (
            <DropdownSection showDivider>
              <DropdownItem
                onPress={onOpen}
                startContent={<MagnifyingGlassIcon className="size-4" />}
                key="copy"
              >
                Impersonate User
              </DropdownItem>
            </DropdownSection>
          ) : null}
          <DropdownSection showDivider>
            <DropdownItem className="pointer-events-none" key="copy">
              {user?.email}
            </DropdownItem>
          </DropdownSection>
          <DropdownItem
            startContent={<ArrowLeftStartOnRectangleIcon className="size-4" />}
            onPress={onLogout}
            key="logout"
          >
            Logout
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      <Modal isOpen={impersonateOpen} onOpenChange={onOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Impersonate User
              </ModalHeader>
              <ModalBody className="pt-4">
                <Autocomplete
                  className="w-full"
                  items={cleanedData}
                  label="Search for a user"
                  placeholder="Enter username, email, organization, or product"
                  onInputChange={(value) => setSearch(value)}
                  onSelectionChange={(key) => handleSelectionChange(key)}
                  inputValue={search}
                  isDisabled={loading}
                >
                  {(item: any) => (
                    <AutocompleteItem
                      key={item.value}
                      className={item.archived ? 'opacity-50' : ''}
                    >
                      {item.label}
                    </AutocompleteItem>
                  )}
                </Autocomplete>
                {selectedUsername && (
                  <Typography
                    variant="body-2"
                    weight="medium"
                    className="text-gray-500 mt-2"
                  >
                    Selected: {selectedUsername}
                  </Typography>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  color="default"
                  variant="light"
                  onPress={() => onClose()}
                >
                  Close
                </Button>
                <Button
                  color="danger"
                  onPress={onImpersonate}
                  isLoading={loading}
                  isDisabled={loading}
                >
                  Impersonate
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default UserMenu;
